import { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Tabs, Tab, Divider, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useUpdateEffect } from "@hooks/shared/use-update-effect";
import useZonesStore from "@stores/client/useZonesStore";
import { TabBarStateContext } from "@context/client/TabBarState";
import { createTabData } from "@utils/client/create-tab-data";

export const TabBar = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const location = useLocation();
  const pathname = location.pathname;
  const [selectedTab, setSelectedTab] = useState(false);
  const [tabItems, setTabItems] = useState([]);
  const { isLoaded, setIsLoaded } = useContext(TabBarStateContext);
  const { zones, fetchItems } = useZonesStore();

  useEffect(() => {
    const safeFetch = async () => {
      try {
        await fetchItems();
      } catch (err) {
        console.warn("Could not fetch zones:", err);
      }
    };
    safeFetch();
  }, [fetchItems]);

  const newTabItems = useMemo(() => {
    if (Array.isArray(zones) && zones.length > 0) {
      return createTabData(zones);
    }
    return [];
  }, [zones]);

  useUpdateEffect(() => {
    if (newTabItems.length > 0) {
      setTabItems(newTabItems);
      setIsLoaded(true);
    }
  }, [newTabItems]);

  useUpdateEffect(() => {
    if (tabItems && tabItems.length > 0) {
      const currentTab = tabItems.find((item) => {
        if (pathname.split("/")[1] === "assets") {
          return pathname.split("/")[2] === item.url.split("/")[2];
        } else {
          const pathnameFirstSegment = pathname.split("/")[1];
          const urlFirstSegment = item.url.split("/")[1];
          return pathnameFirstSegment === urlFirstSegment;
        }
      });
      if (currentTab) {
        setSelectedTab(currentTab.key);
      } else {
        // Default to first tab if no match found
        setSelectedTab(false);
      }
    }
  }, [tabItems, pathname]);

  const handleTabChange = (value) => {
    setSelectedTab(value);
  };

  // Don't render tabs if tabItems haven't loaded yet
  if (tabItems.length === 0) {
    return null;
  }

  return (
    <>
      {isLoaded && (
        <Box
          sx={{
            width: "100%",
          }}
        >
          <Divider
            sx={{ borderColor: alpha(theme.palette.common.white, 0.15) }}
          />
          <Tabs
            indicatorColor="transparent"
            variant="scrollable"
            scrollButtons
            allowScrollButtonsMobile
            value={selectedTab}
            sx={{ width: "100%" }}
          >
            {tabItems.map((item) => (
              <Tab
                sx={{
                  "&.Mui-selected": {
                    backgroundColor: alpha(theme.palette.common.white, 0.1),
                    color: theme.palette.primary.contrastText,
                    fontWeight: 700,
                  },
                  flexGrow: 1,
                  color: theme.palette.common.white,
                }}
                key={item.key}
                label={item.value}
                value={item.key}
                // Labels are tenant zone names (seed data), so tests target
                // a tab by its route instead.
                data-testid="top-nav-tab"
                data-url={item.url}
                onClick={() => {
                  navigate(item.url);
                  handleTabChange(item.key);
                }}
              />
            ))}
          </Tabs>
        </Box>
      )}
    </>
  );
};
